import { ROUTES } from '@/router/routeNames'

/**
 * Composable for handling citation links in Righty messages.
 * Converts markdown-style internal links to Vue Router navigation.
 */
export function useCitationLinks() {
  const route = useRoute()
  const router = useRouter()

  // Get teamPid from current route
  const teamPid = computed(() => route.params.teamPid as string)

  /**
   * Map path patterns to Vue Router route configurations
   */
  function getRouteForPath(path: string): { name: string; params: Record<string, string> } | null {
    // Widget: /widgets/wdg_xxx
    const widgetMatch = path.match(/^widgets\/(wdg_[a-z0-9]+)$/i)
    if (widgetMatch) {
      return { name: ROUTES.WIDGET, params: { teamPid: teamPid.value, widgetId: widgetMatch[1] } }
    }

    // Flow: /flows/ofn_xxx
    const flowMatch = path.match(/^flows\/(ofn_[a-z0-9]+)$/i)
    if (flowMatch) {
      return { name: ROUTES.OFFERFUNNEL, params: { teamPid: teamPid.value, offerFunnelId: flowMatch[1] } }
    }

    // Campaign: /personalizer/cpn_xxx
    const campaignMatch = path.match(/^personalizer\/(cpn_[a-z0-9]+)$/i)
    if (campaignMatch) {
      return { name: ROUTES.CAMPAIGN, params: { teamPid: teamPid.value, campaignId: campaignMatch[1] } }
    }

    // Dimension: /segments?dimension=dim_xxx or /segments/dim_xxx
    const dimensionMatch = path.match(/^segments(?:\?dimension=|\/)(dim_[a-z0-9]+)$/i)
    if (dimensionMatch) {
      return { name: ROUTES.SEGMENTGROUP, params: { teamPid: teamPid.value, segmentGroupId: dimensionMatch[1] } }
    }

    // Offer: /offers/ofr_xxx
    const offerMatch = path.match(/^offers\/(ofr_[a-z0-9]+)$/i)
    if (offerMatch) {
      return { name: ROUTES.OFFER, params: { teamPid: teamPid.value, offerId: offerMatch[1] } }
    }

    // Question: /questions/raq_xxx
    const questionMatch = path.match(/^questions\/(raq_[a-z0-9]+)$/i)
    if (questionMatch) {
      return { name: ROUTES.QUESTION, params: { teamPid: teamPid.value, questionId: questionMatch[1] } }
    }

    // Visitor/People: /people/email%40example.com or /people/vis_xxx
    const visitorMatch = path.match(/^people\/(.+)$/)
    if (visitorMatch) {
      return { name: ROUTES.VISITOR_DETAIL, params: { teamPid: teamPid.value, visitorId: visitorMatch[1] } }
    }

    return null
  }

  /**
   * Handle clicks on internal links in rendered HTML content.
   * Intercepts clicks on elements with data-internal-path attribute.
   */
  function handleCitationClick(event: MouseEvent) {
    const target = event.target as HTMLElement
    const link = target.closest('a[data-internal-path]') as HTMLAnchorElement

    if (link) {
      event.preventDefault()
      const path = link.dataset.internalPath
      if (path) {
        const routeConfig = getRouteForPath(path)
        if (routeConfig) {
          router.push(routeConfig)
        }
      }
    }
  }

  /**
   * Transform markdown-style internal links to HTML with data attributes.
   * Pattern: [Text](/path/id) -> <a data-internal-path="path/id">Text</a>
   */
  function transformCitationLinks(content: string): string {
    return content.replace(
      /\[([^\]]+)\]\(\/([^)]+)\)/g,
      (_match, text, path) => {
        return `<span class="inline-flex items-center gap-1 rounded bg-slate-100 px-1.5 py-0.5 text-xs font-medium text-indigo-600 dark:bg-slate-700 dark:text-indigo-400">${text}</span>`
      }
    )
  }

  return {
    handleCitationClick,
    transformCitationLinks,
    getRouteForPath
  }
}
